import { Card, Col, Form, Row } from "react-bootstrap";
import {
  useGetPriceListQuery,
  useGetUserProfileQuery,
  useUpdateSettingsMutation,
  useGetDefaultSettingsQuery,
  useUpdateDefaultSettingsMutation,
} from "../../features/users/usersApi";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import FullScreenLoader from "../../components/FullScreenLoader";
import { toast } from "react-toastify";
import {
  IoEarthOutline,
  IoGiftOutline,
  IoCalendarOutline,
  IoPricetagOutline,
  IoMailOutline,
  IoPencilOutline,
} from "react-icons/io5";
import { useGetOccasionListQuery } from "../../features/occation/occationApi";
import { useGetEmailTemplateListQuery } from "../../features/emailTemplate/emailTemplateApi";
import { giftTypeOptions } from "../../features/emailTemplate/emailTypes";
import SettingsRow from "./components/SettingsRow";
import {
  NOTE_MAX_LENGTH,
  dividerStyle,
  iconBadgeStyle,
  noteCardStyle,
  noteCounterStyle,
  pageCardBodyStyle,
  pageCardStyle,
  pageContentStyle,
  rowDescriptionStyle,
  rowLabelStyle,
  rowSelectStyle,
  sectionSubtitleStyle,
  sectionTitleStyle,
  settingCardStyle,
} from "./style";

const SettingsPage = () => {
  const internationalShippingId = useId();
  const fallbackGiftId = useId();
  const [updateGiftSetting, { isLoading }] = useUpdateSettingsMutation();
  const { data: profileData, isFetching } = useGetUserProfileQuery({});

  const { data: occasionData } = useGetOccasionListQuery(undefined);
  const { data: emailTemplateData } = useGetEmailTemplateListQuery({});
  const { data: priceData } = useGetPriceListQuery(undefined);
  const { data: defaultSettingsData, isFetching: isFetchingDefaultSettings } =
    useGetDefaultSettingsQuery();
  const [updateDefaultSettings, { isLoading: isSavingDefaultSettings }] =
    useUpdateDefaultSettingsMutation();

  const defaultSettings = defaultSettingsData?.data?.default_settings;

  const [note, setNote] = useState("");
  const noteSaveTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setNote(defaultSettings?.handwritten_note || "");
  }, [defaultSettings?.handwritten_note]);

  useEffect(() => {
    return () => {
      if (noteSaveTimeout.current) {
        clearTimeout(noteSaveTimeout.current);
      }
    };
  }, []);

  const handleInternationalShipping = async (e: any) => {
    await updateGiftSetting({
      is_international_shipping: e.target.checked,
    });
    toast.success("Updated successfully!");
  };

  const handlePrefrenceChange = async (e: any) => {
    await updateGiftSetting({
      is_fallback_gift: e.target.checked,
    });
    toast.success("Updated successfully!");
  };

  const handleDefaultSettingChange = async (
    field: "occasion_id" | "email_template_id" | "gift_type" | "price_id",
    value: string
  ) => {
    if (field === "occasion_id") {
      const templateStillMatches = (emailTemplateData?.data || []).some(
        (template) =>
          template.email_template_id === defaultSettings?.email_template_id &&
          (!template.email_type || template.email_type === "occasional") &&
          String(template.occasion_id) === String(value)
      );

      await updateDefaultSettings({
        occasion_id: value,
        ...(templateStillMatches ? {} : { email_template_id: "" }),
      });
      toast.success("Updated successfully!");
      return;
    }

    await updateDefaultSettings({ [field]: value });
    toast.success("Updated successfully!");
  };

  const pricesForGiftType = (priceData?.data || []).filter((price) =>
    defaultSettings?.gift_type
      ? price.gift_types.includes(defaultSettings.gift_type)
      : true
  );

  const selectedOccasionId = defaultSettings?.occasion_id || "";

  const occasionTemplates = useMemo(() => {
    return (emailTemplateData?.data || []).filter((template) => {
      const isOccasional =
        !template.email_type || template.email_type === "occasional";
      if (!isOccasional) return false;
      if (!selectedOccasionId) return false;
      return String(template.occasion_id) === String(selectedOccasionId);
    });
  }, [emailTemplateData?.data, selectedOccasionId]);

  const handleGiftTypeChange = async (value: string) => {
    const matchingPrice = priceData?.data?.find((price) =>
      price.gift_types.includes(value)
    );

    await updateDefaultSettings({
      gift_type: value,
      ...(matchingPrice ? { price_id: matchingPrice.price_id } : {}),
    });
    toast.success("Updated successfully!");
  };

  const handleNoteChange = (value: string) => {
    setNote(value);

    if (noteSaveTimeout.current) {
      clearTimeout(noteSaveTimeout.current);
    }

    noteSaveTimeout.current = setTimeout(async () => {
      await updateDefaultSettings({ handwritten_note: value });
      toast.success("Updated successfully!");
    }, 500);
  };

  return (
    <Row>
      <Col>
        <Card style={pageCardStyle}>
          <Card.Body style={pageCardBodyStyle}>
            <div style={pageContentStyle}>
              {(isLoading ||
                isFetching ||
                isFetchingDefaultSettings ||
                isSavingDefaultSettings) && <FullScreenLoader />}

              <h5 className="mb-1 fw-semibold" style={sectionTitleStyle}>
                Global Settings
              </h5>
              <p className="mb-3 text-muted" style={sectionSubtitleStyle}>
                Configure global preferences that apply to all gifts.
              </p>

              <Row className="g-3">
                <Col md={6}>
                  <Card style={settingCardStyle}>
                    <Card.Body className="d-flex align-items-start gap-3">
                      <div style={iconBadgeStyle}>
                        <IoEarthOutline />
                      </div>
                      <div className="d-flex flex-grow-1 align-items-start justify-content-between gap-2">
                        <div>
                          <h6 className="mb-1 fw-semibold" style={rowLabelStyle}>
                            International Shipping
                          </h6>
                          <p className="mb-0 text-muted" style={rowDescriptionStyle}>
                            Automatically send gifts to recipients outside of your
                            country without manual approval.
                          </p>
                        </div>
                        <Form.Check
                          type="switch"
                          id={internationalShippingId}
                          checked={
                            profileData?.data?.is_international_shipping as any
                          }
                          onChange={handleInternationalShipping}
                        />
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card style={settingCardStyle}>
                    <Card.Body className="d-flex align-items-start gap-3">
                      <div style={iconBadgeStyle}>
                        <IoGiftOutline />
                      </div>
                      <div className="d-flex flex-grow-1 align-items-start justify-content-between gap-2">
                        <div>
                          <h6 className="mb-1 fw-semibold" style={rowLabelStyle}>
                            Fallback Gift Preferences
                          </h6>
                          <p className="mb-0 text-muted" style={rowDescriptionStyle}>
                            Send a curated surprise gift if the recipient doesn't
                            complete their preferences.
                          </p>
                        </div>
                        <Form.Check
                          type="switch"
                          id={fallbackGiftId}
                          checked={profileData?.data?.is_fallback_gift as any}
                          onChange={handlePrefrenceChange}
                        />
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <hr className="my-4" style={dividerStyle} />

              <h5 className="mb-1 fw-semibold" style={sectionTitleStyle}>
                Default Gift Settings
              </h5>
              <p className="mb-3 text-muted" style={sectionSubtitleStyle}>
                These defaults will be used when creating new gifts or campaigns.
              </p>

              <Card style={settingCardStyle}>
                <Card.Body className="p-0">
                  <SettingsRow
                    icon={<IoCalendarOutline />}
                    title="Default Occasion"
                    description="Choose the default occasion for gifts."
                    control={
                      <Form.Select
                        style={rowSelectStyle}
                        value={defaultSettings?.occasion_id || ""}
                        onChange={(e) =>
                          handleDefaultSettingChange("occasion_id", e.target.value)
                        }
                      >
                        <option value="" disabled>
                          Select occasion
                        </option>
                        {occasionData?.data?.map((occasion) => (
                          <option key={occasion.occasion_id} value={occasion.occasion_id}>
                            {occasion.name}
                          </option>
                        ))}
                      </Form.Select>
                    }
                  />

                  <hr className="m-0" style={dividerStyle} />

                  <SettingsRow
                    icon={<IoGiftOutline />}
                    title="Default Gift Type"
                    description="Select the default type of gift."
                    control={
                      <Form.Select
                        style={rowSelectStyle}
                        value={defaultSettings?.gift_type || ""}
                        onChange={(e) => handleGiftTypeChange(e.target.value)}
                      >
                        <option value="" disabled>
                          Select gift type
                        </option>
                        {giftTypeOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                    }
                  />

                  <hr className="m-0" style={dividerStyle} />

                  <SettingsRow
                    icon={<IoPricetagOutline />}
                    title="Default Price Range"
                    description="Set the default budget for gifts."
                    control={
                      <Form.Select
                        style={rowSelectStyle}
                        value={defaultSettings?.price_id || ""}
                        onChange={(e) =>
                          handleDefaultSettingChange("price_id", e.target.value)
                        }
                      >
                        <option value="" disabled>
                          Select price
                        </option>
                        {pricesForGiftType.map((price) => (
                          <option key={price.price_id} value={price.price_id}>
                            ${price.order_price}
                          </option>
                        ))}
                      </Form.Select>
                    }
                  />

                  <hr className="m-0" style={dividerStyle} />

                  <SettingsRow
                    icon={<IoMailOutline />}
                    title="Default Email Template"
                    description="Select the default email template."
                    control={
                      <Form.Select
                        style={rowSelectStyle}
                        value={
                          occasionTemplates.some(
                            (template) =>
                              template.email_template_id ===
                              defaultSettings?.email_template_id
                          )
                            ? defaultSettings?.email_template_id || ""
                            : ""
                        }
                        disabled={!selectedOccasionId}
                        onChange={(e) =>
                          handleDefaultSettingChange("email_template_id", e.target.value)
                        }
                      >
                        <option value="" disabled>
                          {selectedOccasionId
                            ? "Select template"
                            : "Select occasion first"}
                        </option>
                        {occasionTemplates.map((template) => (
                          <option
                            key={template.email_template_id}
                            value={template.email_template_id}
                          >
                            {template.title}
                          </option>
                        ))}
                      </Form.Select>
                    }
                  />
                </Card.Body>
              </Card>

              <Card style={noteCardStyle}>
                <Card.Body>
                  <div className="d-flex align-items-start gap-3 mb-3">
                    <div style={iconBadgeStyle}>
                      <IoPencilOutline />
                    </div>
                    <div>
                      <h6 className="mb-0 fw-semibold" style={rowLabelStyle}>
                        Default Handwritten Note
                      </h6>
                      <p className="mb-0 text-muted" style={rowDescriptionStyle}>
                        This note will be included in the card for all gifts.
                      </p>
                    </div>
                  </div>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    maxLength={NOTE_MAX_LENGTH}
                    value={note}
                    onChange={(e) => handleNoteChange(e.target.value)}
                  />
                  <p className="mb-0 mt-1 text-muted" style={noteCounterStyle}>
                    {note.length} / {NOTE_MAX_LENGTH} characters
                  </p>
                </Card.Body>
              </Card>
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default SettingsPage;
